import { Link } from "react-router-dom";
import { ArrowRight, Heart, Compass, Sparkles, Leaf } from "lucide-react";
import NewsletterSignup from "@/components/NewsletterSignup";

const ABOUT_IMG =
  "https://customer-assets.emergentagent.com/job_vet-stem-tutoring/artifacts/xx29rlcc_e0aa5ffa-6441-42a1-a69f-c82ae8fe6ae8.jpg";

const VALUES = [
  {
    icon: Heart,
    title: "A supportive learning environment",
    body: "Students learn best in a calm, low-pressure space. Every tutoring session is structured for focus so STEM concepts click faster.",
  },
  {
    icon: Compass,
    title: "Clinically grounded learning",
    body: "I link biology, chemistry, and health science concepts to real veterinary and clinical cases. The result: deeper understanding, stronger retention, and knowledge you can actually use.",
  },
  {
    icon: Sparkles,
    title: "Your story is your strength",
    body: "First-generation and non-traditional applicants bring depth and resilience to their applications. I help you turn that into a stronger admissions story.",
  },
  {
    icon: Leaf,
    title: "A sustainable approach to success",
    body: "STEM success comes from consistency, not burnout. We build study routines that fit your real life so you can keep moving forward.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 lg:pt-24 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-7 animate-fade-up">
          <p className="overline text-terracotta">About Dr. Delmar</p>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1] text-ink mt-4">
            Dr. Ashley Delmar,<span className="text-forest"> DVM, MRCVS.</span>
          </h1>
          <div className="mt-8 space-y-5 text-ink leading-relaxed text-lg max-w-2xl">
            <p>
              As a Doctor of Veterinary Medicine (DVM), I bring a perspective to <strong className="text-forest">STEM education</strong>{" "}
              that is shaped not only by clinical training, but by the realities of building a
              path into veterinary medicine as a first-generation and non-traditional student.
              I also work closely with <strong className="text-forest">neurodivergent students</strong> (ADHD, autism, dyslexia),
              adapting study strategies to how each brain actually learns.
              My approach to academic coaching is grounded in scientific rigor, but equally in
              understanding how students learn, adapt, and succeed in real life.
            </p>
            <p>
              During my own time in veterinary school, I balanced multiple jobs, extracurricular
              commitments, and research while often having to teach myself complex material from
              scratch. That experience shaped the way I teach today. I understand what it means
              to feel stretched too thin, to need efficient and effective study strategies, and to
              work toward ambitious goals without a traditional support system or roadmap.
            </p>
            <p>
              I know firsthand how isolating a demanding program can feel when your brain works
              differently. As a <strong className="text-forest">neurodivergent student</strong>,
              I had to build my own toolkit to get through vet school. It shaped how I teach today.
            </p>
            <p>
              My teaching philosophy focuses on developing true understanding rather than
              memorization alone, bridging complex biological concepts with practical and
              clinical application. Through <strong className="text-forest">The STEM Vet</strong>,
              I provide a structured and supportive approach to mastering STEM subjects and
              preparing for careers in veterinary medicine, medicine, and other health sciences.
              By combining clinical insight with personalized academic coaching, I aim to give
              students the tools to move into their future careers with confidence, clarity, and
              long-term academic resilience.
            </p>
          </div>
          <Link
            to="/contact"
            data-testid="about-cta"
            className="mt-10 inline-flex items-center gap-2 bg-forest text-paper-50 px-6 py-3.5 text-base font-semibold rounded-sm hover:bg-forest-700 hover:-translate-y-0.5 transition-all"
          >
            Let's work together <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="lg:col-span-5 lg:sticky lg:top-28">
          <div className="rounded-sm overflow-hidden border border-paper-200">
            <img src={ABOUT_IMG} alt="Dr. Ashley Delmar, DVM, MRCVS" className="w-full h-[520px] object-cover object-[50%_30%]" />
          </div>
          <div className="mt-4 bg-paper-100 border border-paper-200 p-6 rounded-sm">
            <p className="overline text-forest">Credentials</p>
            <ul className="mt-3 text-sm text-ink space-y-1.5">
              <li>· Doctor of Veterinary Medicine (DVM)</li>
              <li>· Member of the Royal College of Veterinary Surgeons (MRCVS)</li>
              <li>· Specialized academic & admissions coaching</li>
              <li>· MMI interview & essay expertise</li>
              <li>· Published scientific author</li>
              <li>· Global veterinary experience</li>
              <li>· First-generation graduate</li>
            </ul>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="bg-paper-100 border-y border-paper-200">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
          <div className="max-w-2xl">
            <p className="overline text-terracotta">How I coach</p>
            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight text-ink mt-3">
              Four things I believe, and why they shape every session.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-12">
            {VALUES.map((v, i) => (
              <div key={v.title} data-testid={`about-value-${i}`} className="bg-paper-50 border border-paper-200 p-7 rounded-sm">
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-sm bg-forest text-paper-50">
                    <v.icon className="w-5 h-5" />
                  </span>
                  <h3 className="font-display font-bold text-xl tracking-tight text-ink">{v.title}</h3>
                </div>
                <p className="mt-4 text-ink-muted leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* QUOTE */}
      <section className="max-w-4xl mx-auto px-5 sm:px-8 py-24 text-center">
        <p className="font-display font-light text-2xl sm:text-3xl lg:text-4xl leading-snug text-ink tracking-tight">
          "Being 'smart' isn't about understanding everything the first time. It's about
          <span className="text-forest"> curiosity, persistence,</span> and learning how to keep going when things aren't easy."
        </p>
        <p className="mt-6 overline text-terracotta">Dr. Ashley Delmar, DVM, MRCVS</p>
      </section>

      <NewsletterSignup variant="band" testid="about-newsletter" />
    </div>
  );
}
